import React, { useMemo } from 'react';
import { Box, ButtonBase, Collapse, Typography } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import { formatListingDateLabel } from './listingModalDateUtils';

/**
 * Inline date row for listing modals — tap the pill to expand a calendar in place.
 * Value/onChange use YYYY-MM-DD strings (same contract as parseDateInput).
 */
const ListingDateField = ({
  label,
  value = '',
  onChange,
  disabled = false,
  open = false,
  onOpenChange,
  helperText,
  required = false,
  readOnly = false,
}) => {
  const dayValue = useMemo(() => {
    if (!value) return null;
    const parsed = dayjs(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }, [value]);

  const displayLabel = dayValue ? formatListingDateLabel(value) : 'Set date…';
  const interactive = !disabled && !readOnly;

  const handleToggle = () => {
    if (!interactive) return;
    onOpenChange?.(!open);
  };

  const handleSelect = (next) => {
    if (!next || !next.isValid()) return;
    // Keep the calendar day as YYYY-MM-DD — toISOString() would shift the day in UTC+ zones.
    onChange?.(next.format('YYYY-MM-DD'));
    onOpenChange?.(false);
  };

  const pillBorder = open
    ? '1.5px solid rgba(0,103,102,0.55)'
    : dayValue
      ? '1px solid rgba(0,103,102,0.35)'
      : '1px solid rgba(0,0,0,0.1)';

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          minHeight: 44,
        }}
      >
        <Typography
          component="label"
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: disabled || readOnly ? 'rgba(0,0,0,0.38)' : 'rgba(0,0,0,0.55)',
            flexShrink: 0,
          }}
        >
          {label}
          {required ? (
            <Typography component="span" sx={{ color: 'rgba(0,0,0,0.45)', ml: 0.25 }}>
              *
            </Typography>
          ) : null}
        </Typography>

        <ButtonBase
          onClick={handleToggle}
          disabled={!interactive}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup={interactive ? 'dialog' : undefined}
          sx={{
            borderRadius: '999px',
            flexShrink: 0,
            opacity: disabled || readOnly ? 0.55 : 1,
            cursor: interactive ? 'pointer' : 'default',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.65,
              borderRadius: '999px',
              border: pillBorder,
              bgcolor: open ? 'rgba(0,103,102,0.06)' : 'rgba(255,255,255,0.85)',
              minWidth: 112,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: dayValue ? 500 : 400,
                color: dayValue ? 'rgba(0,0,0,0.82)' : 'rgba(0,0,0,0.42)',
                lineHeight: 1.25,
              }}
            >
              {displayLabel}
            </Typography>
          </Box>
        </ButtonBase>
      </Box>

      <Collapse in={open && interactive} unmountOnExit>
        <Box
          sx={{
            mt: 1,
            mb: 0.5,
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: 'rgba(255,255,255,0.95)',
            overflow: 'hidden',
            boxShadow: '0 8px 28px rgba(0,0,0,0.06)',
          }}
        >
          <DateCalendar
            value={dayValue}
            onChange={handleSelect}
            sx={{
              width: '100%',
              maxWidth: '100%',
              mx: 'auto',
              '& .MuiPickersCalendarHeader-label': { fontSize: '0.9375rem' },
              '& .MuiDayCalendar-weekDayLabel': { fontSize: '0.75rem' },
              '& .MuiPickersDay-root': {
                fontSize: '0.875rem',
                width: 36,
                height: 36,
              },
              '& .MuiPickersDay-root.Mui-selected': {
                bgcolor: 'rgba(0,103,102,0.92)',
                '&:hover, &:focus': { bgcolor: 'rgba(0,103,102,0.85)' },
              },
              '& .MuiPickersDay-today': {
                borderColor: 'rgba(0,103,102,0.45)',
              },
            }}
          />
          <Typography
            sx={{
              px: 2,
              pb: 1.5,
              fontSize: '0.72rem',
              color: 'rgba(0,0,0,0.45)',
              lineHeight: 1.45,
              textAlign: 'center',
            }}
          >
            Tap a day to set it. Use the arrows to change month.
          </Typography>
        </Box>
      </Collapse>

      {helperText ? (
        <Typography
          sx={{
            mt: 0.75,
            fontSize: '0.72rem',
            color: 'rgba(0,0,0,0.45)',
            lineHeight: 1.45,
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ListingDateField;
